<template>
  <div class="worklog-page">
    <van-nav-bar :border="false" :title="pageTitle" left-arrow @click-left="$router.back()"/>
    <section class="profile-strip">
      <div><span>事业部</span><b>{{ session.departmentName }}</b></div>
      <div><span>岗位</span><b>{{ session.positionName }}</b></div>
      <div><span>姓名</span><b>{{ session.employeeName }}</b></div>
      <p>{{ displayDate }}</p>
    </section>
    <section v-if="draftReason" class="return-tip">
      <van-icon name="warning-o"/>
      <span>该日志曾被驳回，请修改后重新提交。<em>驳回说明：{{ draftReason }}</em></span></section>
    <van-skeleton v-if="loading" title :row="6" class="form-skeleton"/>
    <form v-else class="dynamic-form" @submit.prevent="submit">
      <section v-if="isDaily" class="field-card execution-binding">
        <h2>关联执行任务</h2>
        <van-field readonly clickable :value="selectedTaskTitle" :placeholder="tasks.length?'选择本日志对应的任务':'暂无已分配任务，可继续填写'"
                   right-icon="arrow" class="task-picker" @click="tasks.length&&(showTasks=true)"/>
        <van-field v-if="selectedSubtaskOptions.length" readonly clickable :value="selectedSubtaskTitle" placeholder="选择子任务（可选）"
                   right-icon="arrow" class="task-picker" @click="showSubtasks=true"/>
        <div v-if="selectedTask" class="task-binding-panel">
          <div class="task-binding-head">
            <span class="task-type-tag" :class="isQuantitativeTask ? 'quant' : 'normal'">{{ isQuantitativeTask ? '可量化任务' : '非量化任务' }}</span>
            <span class="task-rate">当前完成 {{ taskProgressLabel }}</span>
          </div>
          <div class="task-binding-block">
            <label>任务目标</label>
            <p>{{ taskObjectiveText }}</p>
          </div>
          <div v-if="taskCriteriaText" class="task-binding-block muted">
            <label>验收标准</label>
            <p>{{ taskCriteriaText }}</p>
          </div>
          <div v-if="isQuantitativeTask" class="task-progress-hint">
            <van-icon name="info-o"/>
            <p>量化任务进度将依据下方「量化成果」字段自动累计，无需手动拖动进度条。</p>
          </div>
          <div v-else class="task-progress-slider">
            <div class="slider-head">
              <label>今日进度自报</label>
              <strong>{{ reportedCompletionRate }}%</strong>
            </div>
            <van-slider v-model="reportedCompletionRate" :min="0" :max="100" :step="1" active-color="#d32f2f" inactive-color="#f0d4d4"/>
            <div class="slider-track">
              <div class="slider-fill" :style="{width: reportedCompletionRate + '%'}"></div>
            </div>
            <p class="slider-foot">提交后由主管复核生效；请如实反映今日推进幅度</p>
          </div>
        </div>
        <p v-else class="binding-hint">当前尚未建立战略、项目或任务，可先按自主工作填写；后续建立任务后可补充关联。</p>
      </section>
      <van-popup v-model="showTasks" position="bottom" round>
        <van-picker show-toolbar title="选择日志对应任务" :columns="taskColumns" @cancel="showTasks=false" @confirm="chooseTask"/>
      </van-popup>
      <van-popup v-model="showSubtasks" position="bottom" round>
        <van-picker show-toolbar title="选择子任务" :columns="subtaskColumns" @cancel="showSubtasks=false" @confirm="chooseSubtask"/>
      </van-popup>
      <!-- 今日工作日志（日报） -->
      <section v-if="hasDailyLogs" class="field-card">
        <h2>今日工作日志</h2>
        <div class="daily-tabs" role="tablist">
          <button type="button" :class="{active: dailyTab === 'today'}" @click="dailyTab = 'today'">今日总结</button>
          <button type="button" :class="{active: dailyTab === 'tomorrow'}" @click="dailyTab = 'tomorrow'">明日计划</button>
        </div>
        <div class="daily-logs-wrapper">
          <div v-show="dailyTab === 'today'" class="daily-logs-inputs">
            <div v-for="(entry,idx) in dailyLogEntries" :key="`daily-${idx}`" class="daily-log-entry">
              <label :for="`dailyLog-${idx}`">第 {{ idx + 1 }} 条<em v-if="idx===0">*</em></label>
              <textarea :id="`dailyLog-${idx}`" v-model.trim="dailyLogEntries[idx].value" :maxlength="600" rows="4"
                        :placeholder="`请填写第 ${idx+1} 条工作日志（建议：做了什么、结果如何、后续计划）`"></textarea>
            </div>
          </div>
          <button v-show="dailyTab === 'today'" type="button" class="add-entry-btn" @click="addEntry(dailyLogEntries)">+ 增加一条今日日志</button>
          <div v-show="dailyTab === 'tomorrow'" class="tomorrow-plans">
            <h4>明天工作计划</h4>
            <div class="daily-logs-inputs">
              <div v-for="(entry,idx) in tomorrowPlanEntries" :key="`plan-${idx}`" class="daily-log-entry">
                <label :for="`tomorrowPlan-${idx}`">第 {{ idx + 1 }} 条<em v-if="idx===0">*</em></label>
                <textarea :id="`tomorrowPlan-${idx}`" v-model.trim="tomorrowPlanEntries[idx].value" :maxlength="600" rows="4"
                          :placeholder="`请填写第 ${idx+1} 条明天计划`"></textarea>
              </div>
            </div>
            <button type="button" class="add-entry-btn" @click="addEntry(tomorrowPlanEntries)">+ 增加一条明天计划</button>
          </div>
        </div>
      </section>

      <!-- 本周总结（周报，复用后端 MONTHLY 模板存储） -->
      <section v-if="isWeekly" class="field-card">
        <h2>工作周报</h2>
        <div class="daily-tabs" role="tablist">
          <button type="button" :class="{active: weeklyTab === 'thisWeek'}" @click="weeklyTab = 'thisWeek'">本周工作总结</button>
          <button type="button" :class="{active: weeklyTab === 'nextWeek'}" @click="weeklyTab = 'nextWeek'">下周工作计划</button>
        </div>
        <div class="weekly-wrapper">
          <div v-show="weeklyTab === 'thisWeek'" class="weekly-inputs">
            <div v-for="(entry,idx) in weeklyEntries" :key="`weekly-${idx}`" class="weekly-log-entry">
              <label :for="`weekly-${idx}`">第 {{ idx + 1 }} 条<em v-if="idx===0">*</em></label>
              <textarea :id="`weekly-${idx}`" v-model.trim="weeklyEntries[idx].value" :maxlength="600" rows="4"
                        :placeholder="`请填写第 ${idx+1} 条本周工作`"></textarea>
            </div>
          </div>
          <button v-show="weeklyTab === 'thisWeek'" type="button" class="add-entry-btn" @click="addEntry(weeklyEntries)">+ 增加一条本周工作</button>
          <div v-show="weeklyTab === 'nextWeek'" class="weekly-inputs">
            <div v-for="(entry,idx) in nextWeekEntries" :key="`nextWeek-${idx}`" class="weekly-log-entry">
              <label :for="`nextWeek-${idx}`">第 {{ idx + 1 }} 条<em v-if="idx===0">*</em></label>
              <textarea :id="`nextWeek-${idx}`" v-model.trim="nextWeekEntries[idx].value" :maxlength="600" rows="4"
                        :placeholder="`请填写第 ${idx+1} 条下周计划`"></textarea>
            </div>
          </div>
          <button v-show="weeklyTab === 'nextWeek'" type="button" class="add-entry-btn" @click="addEntry(nextWeekEntries)">+ 增加一条下周计划</button>
        </div>
      </section>

      <!-- 模板其他字段 -->
      <section v-for="(group,index) in groups" v-if="(!isDaily || dailyTab === 'today') && (!isWeekly || weeklyTab === 'thisWeek')" :key="index" class="field-card">
        <h2>{{ group.title }}</h2>
        <div v-for="field in group.fields" :key="field.code" class="field-row" :class="{vertical:field.type==='LONG_TEXT'}">
          <label :for="field.code">{{ field.label }}<em v-if="field.required">*</em></label>
          <textarea v-if="field.type==='LONG_TEXT'" :id="field.code" v-model.trim="values[field.code]" :maxlength="field.maxLength||4000" rows="7"
                    :placeholder="placeholder(field)"></textarea>
          <div v-if="field.code==='performanceWorkFacts'" class="writing-guide">
            <b>每项工作单独一条</b><span>1. 做了什么：具体事项、对象/客户/渠道及采取的动作</span><span>2. 做得怎么样：完成进度、质量、问题与处理结果</span><span>3. 数据怎么样：数量、金额、播放、粉丝、表单、成交或转化等结果</span><span>4. 与哪个量化指标有关；未完成时写明原因和下一步</span>
          </div>
          <div v-if="field.type!=='LONG_TEXT'" class="input-shell"><input :id="field.code" v-model.trim="values[field.code]"
                                                                          :type="field.type==='NUMBER'?'number':'text'" inputmode="decimal"
                                                                          placeholder="请输入"/><span>{{ unit(field.code) }}</span></div>
          <p v-if="field.targetLabel||field.scoringRule" class="field-hint"><span v-if="field.targetLabel">目标 {{ field.targetLabel }}</span><span
            v-if="field.trialTargetLabel">试用期 {{ field.trialTargetLabel }}</span><span
            v-if="field.weight">权重 {{ Math.round(Number(field.weight) * 100) }}%</span><b v-if="field.scoringRule">{{ field.scoringRule }}</b></p>
        </div>
      </section>

      <section v-if="isDaily && session.positionFamily === 'SALES'" class="field-card collaborator-card">
        <h2>营收配合人</h2>
        <p class="collaborator-note">按实际贡献顺序选择同事，最多 5 人；填写回款、成交等创收数据后必须选满 5 人，系统依次记录权重 1.0、0.9、0.8、0.7、0.6。</p>
        <div class="collaborator-selects">
          <label v-for="(rank,index) in collaboratorWeights" :key="rank">第 {{ index + 1 }} 位（权重 {{ rank }}）
            <select v-model="collaboratorEmployeeIds[index]" @change="normalizeCollaborators">
              <option :value="null">请选择配合人</option>
              <option v-for="person in collaboratorOptions" :key="person.employeeId" :value="person.employeeId"
                      :disabled="collaboratorEmployeeIds.includes(person.employeeId) && collaboratorEmployeeIds[index] !== person.employeeId">
                {{ person.employeeName }} · {{ person.departmentName || '' }} · {{ person.positionName || '' }}
              </option>
            </select>
          </label>
        </div>
        <p v-if="needsCollaborators && collaboratorEmployeeIds.filter(Boolean).length < 5" class="collaborator-required">当前已填写创收数据，还需选择
          {{ 5 - collaboratorEmployeeIds.filter(Boolean).length }} 名配合人</p>
        <div v-if="collaboratorEmployeeIds.filter(Boolean).length" class="collaborator-order">
          <span v-for="(id,index) in collaboratorEmployeeIds.filter(Boolean)" :key="id">{{ index + 1 }}. {{
              collaboratorName(id)
            }} × {{ collaboratorWeights[index] }}</span>
        </div>
      </section>

      <button type="button" class="duty-float" aria-label="查看岗位职责" @click="dutyVisible = true">
        <van-icon name="description"/>
        <span>岗位<br>职责</span>
      </button>

      <van-popup v-model="dutyVisible" round :style="{ width: '82%', maxHeight: '70%' }">
        <div class="duty-popup">
          <div class="duty-popup-title"><b>岗位职责</b>
            <van-icon name="cross" @click="dutyVisible = false"/>
          </div>
          <p>{{ positionDuty || "暂未配置岗位职责描述，可在「我的 - 岗位职责」中维护。" }}</p>
        </div>
      </van-popup>

      <div class="submit-area">
        <van-button round block type="info" native-type="submit" :loading="submitting" loading-text="提交中">提交</van-button>
      </div>
    </form>
  </div>
</template>

<script>
/**
 * 工作日志填写页（日报 / 本周总结 / 月度总结）。
 * 前端 WEEKLY 映射后端 MONTHLY 模板；动态加号最多 5 条；岗位职责来自 legacy JSONP。
 */
import coreapi from "@/utils/coreapi";

export default {
  data() {
    return {
      loading: true,
      submitting: false,
      template: null,
      session: {},
      values: {},
      draftReason: "",
      dutyVisible: false,
      dailyTab: "today",
      weeklyTab: "thisWeek",
      dailyLogEntries: [{value: ""}, {value: ""}, {value: ""}],
      tomorrowPlanEntries: [{value: ""}, {value: ""}, {value: ""}],
      weeklyEntries: [{value: ""}, {value: ""}, {value: ""}],
      nextWeekEntries: [{value: ""}, {value: ""}, {value: ""}],
      positionDuty: "",
      tasks: [],
      selectedTaskId: "",
      selectedSubtaskId: "",
      showTasks: false,
      showSubtasks: false,
      reportedCompletionRate: 0,
      collaboratorOptions: [],
      collaboratorEmployeeIds: [],
      collaboratorWeights: ["1.0", "0.9", "0.8", "0.7", "0.6"]
    }
  },
  computed: {
    cadence() {
      return (this.$route.query.cadence || "DAILY").toUpperCase()
    },
    // 路由 WEEKLY → 后端 MONTHLY（复用月度字段存周报 JSON）
    apiCadence() {
      return this.cadence === "WEEKLY" ? "MONTHLY" : this.cadence
    },
    isDaily() {
      return this.cadence === "DAILY"
    },
    isWeekly() {
      return this.cadence === "WEEKLY"
    },
    pageTitle() {
      return this.isWeekly ? "本周总结" : this.isDaily ? "工作日志" : "月度总结"
    },
    date() {
      return this.$route.query.date
    },
    displayDate() {
      const p = (this.date || "").split("-");
      return p.length === 3 ? `${p[0]}年${Number(p[1])}月${Number(p[2])}日` : ""
    },
    // 销售/品牌月度模板可能复用日报字段做量化汇总，但周报页面不得渲染日报填写区。
    hasDailyLogs() {
      return this.isDaily && (this.template && this.template.fields || []).some(f => f.code === "dailyLogs")
    },
    // 量化字段与长文本分组；周报隐藏 monthlyAchievements/nextMonthPlan 原生表单项（改由动态列表）
    groups() {
      if (!this.template) return [];
      const number = this.template.fields.filter(f => f.type === "NUMBER");
      const text = this.template.fields.filter(f => f.type !== "NUMBER" && f.code !== "dailyLogs" && !(this.isWeekly && (f.code === "monthlyAchievements" || f.code === "nextMonthPlan")));
      return [number.length ? {
        title: "量化成果",
        fields: number
      } : null, text.length ? {
        title: this.isWeekly ? "其他总结" : (this.cadence === "MONTHLY" ? "总结与计划" : "工作总结"),
        fields: text
      } : null].filter(Boolean)
    },
    needsCollaborators() {
      if (!this.isDaily || this.session.positionFamily !== "SALES") return false;
      return Object.keys(this.values).some(code => /(revenue|recovered|amount|payment|sales|deal)/i.test(code) && Number(this.values[code] || 0) > 0)
    }, taskColumns() {
      return this.tasks.map(x => x.title)
    },
    subtaskColumns() {
      return this.selectedSubtaskOptions.map(x => x.title)
    },
    selectedSubtaskOptions() {
      const task = this.selectedTask;
      if (!task) return [];
      return task.subtasks || task.SUBTASKS || [];
    },
    selectedTask() {
      return this.tasks.find(x => Number(x.id) === Number(this.selectedTaskId))
    },
    selectedSubtask() {
      return this.selectedSubtaskOptions.find(x => Number(x.id) === Number(this.selectedSubtaskId))
    },
    selectedSubtaskTitle() {
      return this.selectedSubtask ? this.selectedSubtask.title : ""
    },
    isQuantitativeTask() {
      const target = this.selectedSubtask || this.selectedTask;
      if (!target) return false;
      const type = target.progress_type || target.progressType || target.task_type || target.taskType;
      return String(type).toUpperCase() === "QUANTITATIVE";
    },
    taskProgressLabel() {
      const target = this.selectedSubtask || this.selectedTask;
      if (!target) return "0%";
      const rate = target.completion_rate != null ? target.completion_rate
        : (target.completionRate != null ? target.completionRate : 0);
      return `${Number(rate)}%`;
    },
    quantitativeIncrement() {
      if (!this.template || !this.isQuantitativeTask) return null;
      const sum = (this.template.fields || [])
        .filter(f => f.type === "NUMBER")
        .reduce((acc, f) => acc + Number(this.values[f.code] || 0), 0);
      return sum > 0 ? sum : null;
    },
    selectedTaskTitle() {
      return this.selectedTask ? this.selectedTask.title : ""
    },
    taskObjectiveText() {
      var target = this.selectedSubtask || this.selectedTask;
      if (!target) return "";
      return target.objective || target.description || "以主管确认的任务目标为准";
    },
    taskCriteriaText() {
      var target = this.selectedSubtask || this.selectedTask;
      if (!target) return "";
      return target.acceptance_criteria || target.acceptanceCriteria || "";
    }
  },
  mounted() {
    this.initialize()
  },
  watch: {
    '$route.fullPath'() {
      this.initialize()
    }
  },
  methods: {
    async initialize() {
      this.loading = true;
      this.submitting = false;
      this.template = null;
      this.values = {};
      this.draftReason = "";
      this.collaboratorEmployeeIds = [];
      this.dailyLogEntries = this._toEntries([], 3);
      this.tomorrowPlanEntries = this._toEntries([], 3);
      this.weeklyEntries = this._toEntries([], 3);
      this.nextWeekEntries = this._toEntries([], 3);
      try {
        const month = this.date.slice(0, 7);
        const workspace = await coreapi.workspace(month);
        this.session = workspace.employee;
        this.template = await coreapi.template(this.date, this.apiCadence);
        this.template.fields.forEach(f => this.$set(this.values, f.code, ""));
        this.splitDailyLogs();
        this.splitWeeklyLogs();
        await this.loadDraft(month);
        this.positionDuty = await coreapi.userDuty();
        if (this.isDaily) {
          this.tasks = await coreapi.availableTasks().catch(() => []);
          if (this.tasks.length === 1) this.selectedTaskId = this.tasks[0].id
        }
        if (this.isDaily && this.session.positionFamily === "SALES") this.collaboratorOptions = await coreapi.collaboratorOptions();
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.loading = false
      }
    },
    today() {
      const value = new Date();
      return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
    },
    /**
     * 反写被驳回（RETURN → DRAFT）的已填数据：查询当月日志，
     * 命中同日期同频次且状态为 DRAFT 的草稿时，把 fieldValues 回填到表单；
     * 若状态为 SUBMITTED 则说明已提交且未被驳回，拦截重复填写。
     */
    async loadDraft(month) {
      let rows = [];
      try {
        rows = await coreapi.history(month)
      } catch (e) {
        return
      }
      const draft = rows.find(item => item.workDate === this.date && item.cadence === this.apiCadence);
      if (!draft) return;
      if (draft.id && this.isDaily && this.session.positionFamily === "SALES") {
        try {
          const selected = await coreapi.worklogCollaborators(draft.id);
          this.collaboratorEmployeeIds = (selected || []).map(v => Number(v.employeeId));
        } catch (ignore) {
        }
      }
      if (draft.status === "SUBMITTED") {
        this.$toast.fail("该日期日志已提交，无需重复填写");
        this.$router.replace("/efficiency");
        return
      }
      Object.keys(draft.fieldValues || {}).forEach(key => this.$set(this.values, key, draft.fieldValues[key]));
      this.draftReason = (draft.review && draft.review.action === "RETURN" && draft.review.reason) || "该日志曾被驳回";
      this.splitDailyLogs();
      this.splitWeeklyLogs();
    },
    unit(code) {
      const field = (this.template.fields || []).find(item => item.code === code);
      return field && field.unit || ""
    },
    /** 动态加号：每条列表最多 5 条 */
    addEntry(list) {
      if (list.length >= 5) {
        this.$toast("最多填写 5 条");
        return
      }
      list.push({value: ""})
    },
    /** 解析 dailyLogs JSON：{today,tomorrow} 或历史三元素数组 */
    splitDailyLogs() {
      const raw = (this.values.dailyLogs || "").trim();
      if (!raw) {
        this.dailyLogEntries = [{value: ""}, {value: ""}, {value: ""}];
        this.tomorrowPlanEntries = [{value: ""}, {value: ""}, {value: ""}];
        return
      }
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          this.dailyLogEntries = this._toEntries(parsed.today || [], 3);
          this.tomorrowPlanEntries = this._toEntries(parsed.tomorrow || [], 3);
          return
        }
        if (Array.isArray(parsed) && parsed.length === 3) {
          this.dailyLogEntries = parsed.map(v => ({value: String(v || "")}));
          this.tomorrowPlanEntries = [{value: ""}, {value: ""}, {value: ""}];
          return
        }
      } catch (e) {
      }
      this.dailyLogEntries = [{value: raw}, {value: ""}, {value: ""}];
      this.tomorrowPlanEntries = [{value: ""}, {value: ""}, {value: ""}]
    },
    /** 读取新结构的两个模板字段，同时兼容历史上合并在 monthlyAchievements 中的周报 JSON。 */
    splitWeeklyLogs() {
      const raw = (this.values.monthlyAchievements || "").trim();
      const nextRaw = (this.values.nextMonthPlan || "").trim();
      let thisWeek = [];
      let nextWeek = [];
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            thisWeek = parsed.thisWeek || [];
            nextWeek = parsed.nextWeek || []
          } else if (Array.isArray(parsed)) {
            thisWeek = parsed
          }
        } catch (e) {
          thisWeek = raw.split(/\r?\n/).filter(Boolean)
        }
      }
      if (nextRaw) {
        try {
          const parsed = JSON.parse(nextRaw);
          nextWeek = Array.isArray(parsed) ? parsed : (parsed.nextWeek || nextWeek)
        } catch (e) {
          nextWeek = nextRaw.split(/\r?\n/).filter(Boolean)
        }
      }
      this.weeklyEntries = this._toEntries(thisWeek, 3);
      this.nextWeekEntries = this._toEntries(nextWeek, 3)
    },
    _toEntries(arr, n) {
      const entries = (arr || []).slice(0, n).map(v => ({value: String(v || "")}));
      while (entries.length < n) entries.push({value: ""});
      return entries
    },
    placeholder(field) {
      if (field.code === "dailyLogs") return "请填写今天完成的三项工作";
      if (field.code === "monthlyAchievements") return "请填写本周完成的三项重点工作";
      if (field.code === "nextMonthPlan") return "请填写下周三项重点工作计划";
      if (field.code !== "performanceWorkFacts") return `请输入${field.label}`;
      return "示例：1. 跟进华星公司招生客户12人，逐一确认课程需求并发送方案；已完成10人沟通，3人提交报名资料，预计回款2.4万元，关联招生人数、招商回款指标。2. 发布短视频3条，完成字幕和音量复核；当日新增播放8,600次、粉丝42人，获得有效表单6条；关联视频制作、播放量、粉丝增长和表单指标。"
    },
    /** 前 requiredCount 条必填且满足 minLen；后续选填项若填写也须达标 */
    validEntries(list, minLen, requiredCount, name) {
      const required = list.slice(0, requiredCount);
      const missing = required.findIndex(v => v.value.trim().length < minLen);
      if (missing >= 0) {
        this.$toast(`第 ${missing + 1} 条${name}至少填写 ${minLen} 个字`);
        return false
      }
      const optionalInvalid = list.slice(requiredCount).findIndex(v => v.value.trim() && v.value.trim().length < minLen);
      if (optionalInvalid >= 0) {
        this.$toast(`第 ${requiredCount + optionalInvalid + 1} 条${name}至少填写 ${minLen} 个字`);
        return false
      }
      return true
    },
    validDailyLogs() {
      if (!this.hasDailyLogs) return true;
      return this.validEntries(this.dailyLogEntries, 10, 1, "工作日志") && this.validEntries(this.tomorrowPlanEntries, 5, 1, "明天计划")
    },
    validWeekly() {
      if (!this.isWeekly) return true;
      return this.validEntries(this.weeklyEntries, 10, 1, "本周工作") && this.validEntries(this.nextWeekEntries, 5, 1, "下周计划")
    },
    /** 绩效工作事实：字数 + 至少含数字（结果数据） */
    validWorkFacts() {
      const text = (this.values.performanceWorkFacts || "").trim();
      if (!text) return true;
      if (text.length < 40) {
        this.$toast("工作日志至少40字，请详细说明工作、完成情况和结果数据");
        return false
      }
      if (!/\d/.test(text)) {
        this.$toast("请填写至少一项结果数据；暂无结果时填写当前进度数据");
        return false
      }
      return true
    },
    normalizeCollaborators() {
      this.collaboratorEmployeeIds = this.collaboratorWeights.map((_, i) => this.collaboratorEmployeeIds[i] || null)
    },
    collaboratorName(id) {
      const person = this.collaboratorOptions.find(v => v.employeeId === id);
      return person ? person.employeeName : ""
    },
    isWeeklyFieldHidden(code) {
      if (!this.isWeekly) return false;
      return code === "monthlyAchievements" || code === "nextMonthPlan"
    },
    chooseTask(value, index) {
      this.selectedTaskId = this.tasks[index].id;
      this.selectedSubtaskId = "";
      const task = this.tasks[index];
      this.reportedCompletionRate = Number((task && (task.completion_rate != null ? task.completion_rate : task.completionRate)) || 0);
      this.showTasks = false
    },
    chooseSubtask(value, index) {
      const sub = this.selectedSubtaskOptions[index];
      this.selectedSubtaskId = sub ? sub.id : "";
      this.reportedCompletionRate = Number((sub && (sub.completion_rate != null ? sub.completion_rate : sub.completionRate)) || 0);
      this.showSubtasks = false
    },
    async submit() {
      if (this.hasDailyLogs) {
        this.values.dailyLogs = JSON.stringify({
          today: this.dailyLogEntries.map(v => v.value.trim()),
          tomorrow: this.tomorrowPlanEntries.map(v => v.value.trim())
        })
      }
      if (this.isWeekly) {
        const thisWeek = this.weeklyEntries.map(v => v.value.trim()).filter(Boolean);
        const nextWeek = this.nextWeekEntries.map(v => v.value.trim()).filter(Boolean);
        this.values.monthlyAchievements = JSON.stringify({thisWeek});
        this.values.nextMonthPlan = nextWeek.join("\n")
      }
      const missing = this.template.fields.find(f => f.required && !this.isWeeklyFieldHidden(f.code) && !this.values[f.code]);
      if (missing) {
        this.$toast(`请填写${missing.label}`);
        return
      }
      if (!this.validDailyLogs() || !this.validWeekly() || !this.validWorkFacts()) return;
      const selectedCollaborators = this.collaboratorEmployeeIds.filter(Boolean);
      if (this.needsCollaborators && selectedCollaborators.length !== 5) {
        this.$toast("本次填写了创收数据，请按贡献顺序选择5名配合人");
        return
      }
      this.submitting = true;
      try {
        const taskItems = this.selectedTask ? [{
          taskId: Number(this.selectedTask.id),
          taskTitle: this.selectedTask.title,
          subtaskId: this.selectedSubtask ? Number(this.selectedSubtask.id) : null,
          subtaskTitle: this.selectedSubtask ? this.selectedSubtask.title : null,
          costHours: 0,
          output: "日志内容见结构化字段",
          evidenceReference: "",
          responsibilityType: "OWNER",
          reportedCompletionRate: this.isQuantitativeTask ? null : Number(this.reportedCompletionRate),
          reportedQuantity: this.quantitativeIncrement
        }] : [];
        await coreapi.submit({
          date: this.date,
          cadence: this.apiCadence,
          templateId: this.template.id,
          fieldValues: this.values,
          collaboratorEmployeeIds: selectedCollaborators,
          taskItems
        });
        this.$toast.success("提交成功");
        setTimeout(() => this.$router.replace("/efficiency"), 500)
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.submitting = false
      }
    }
  }
};
</script>
<style lang="less" scoped>
@import "~@/assets/base.less";

.worklog-page {
  min-height: 100vh;
  padding-bottom: 2.4rem;
  background: #f6f7fb;
  color: #333
}

.profile-strip {
  padding: .38rem .42rem .3rem;
  display: grid;
  grid-template-columns:repeat(3, minmax(0, 1fr));
  background: #fff;
  box-shadow: 0 .05rem .2rem rgba(211, 47, 47, .08)
}

.profile-strip div {
  min-width: 0;
  padding: 0 .18rem;
  border-left: 1PX solid #eee
}

.profile-strip div:first-child {
  border-left: 0;
  padding-left: 0
}

.profile-strip span, .profile-strip b {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap
}

.profile-strip span {
  color: #999bad;
  font-size: .26rem
}

.profile-strip b {
  margin-top: .08rem;
  font-size: .34rem;
  font-weight: 600
}

.profile-strip p {
  grid-column: 1/-1;
  margin-top: .28rem;
  padding-top: .22rem;
  color: @color;
  font-size: .29rem;
  border-top: 1PX solid #f0f0f0
}

.form-skeleton {
  margin: .3rem .28rem;
  padding: .5rem .4rem;
  background: #fff;
  border-radius: .24rem
}

.return-tip {
  margin: .22rem .28rem 0;
  padding: .24rem .28rem;
  display: flex;
  align-items: flex-start;
  gap: .16rem;
  color: #c0392b;
  font-size: .28rem;
  line-height: 1.55;
  background: #fff5f5;
  border: 1PX solid #f3c8c8;
  border-radius: .18rem
}

.return-tip .van-icon {
  margin-top: .06rem;
  font-size: .32rem
}

.return-tip em {
  display: block;
  margin-top: .04rem;
  color: #a56a6a;
  font-style: normal
}

.dynamic-form {
  padding: .3rem .28rem 0
}

.field-card {
  margin-bottom: .3rem;
  padding: .4rem;
  background: #fff;
  border-radius: .24rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1)
}

.field-card h2 {
  position: relative;
  padding-left: .22rem;
  font-size: .4rem;
  letter-spacing: 0
}

.field-card h2:before {
  content: '';
  position: absolute;
  left: 0;
  top: .09rem;
  width: .08rem;
  height: .38rem;
  border-radius: .05rem;
  background: @color
}

.field-row {
  min-height: 1.2rem;
  display: flex;
  align-items: center;
  border-top: 1PX solid #eee
}

.field-row:nth-child(2) {
  margin-top: .2rem;
  border-top: 0
}

.field-row label {
  width: 3.05rem;
  flex: 0 0 3.05rem;
  color: #55586c;
  font-size: .33rem
}

.field-row em {
  margin-left: .05rem;
  color: #ee5c5c;
  font-style: normal
}

.input-shell {
  flex: 1;
  display: flex;
  align-items: center;
  border: 1PX solid #dddfea;
  border-radius: .14rem;
  background: #fafbfe
}

.input-shell:focus-within {
  border-color: @color;
  box-shadow: 0 0 0 2PX rgba(78, 84, 199, .08)
}

.input-shell input {
  min-width: 0;
  width: 100%;
  height: .78rem;
  padding: 0 .2rem;
  font-size: .34rem
}

.input-shell span {
  min-width: .55rem;
  color: #77798a;
  font-size: .3rem
}

.field-row.vertical {
  display: block;
  padding-top: .32rem
}

.field-row.vertical label {
  width: auto;
  display: block
}

.field-row textarea {
  width: 100%;
  min-height: 2.8rem;
  margin-top: .25rem;
  padding: .25rem;
  color: #44475b;
  font-size: .33rem;
  line-height: 1.65;
  border: 1PX solid #dddfea;
  border-radius: .14rem;
  background: #fafbfe
}

.field-row textarea:focus {
  border-color: @color
}

.submit-area {
  position: fixed;
  z-index: 5;
  left: 0;
  right: 0;
  bottom: 0;
  max-width: 640px;
  margin: auto;
  padding: .25rem .42rem calc(.25rem + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, .96);
  box-shadow: 0 -.05rem .25rem rgba(211, 47, 47, .1)
}

.submit-area /deep/ .van-button {
  height: 1.05rem;
  background: linear-gradient(135deg, #6369dd, #B71C1C);
  border: 0;
  font-size: .38rem
}

.field-row {
  flex-wrap: wrap
}

.field-hint {
  box-sizing: border-box;
  width: 100%;
  padding: .05rem 0 .18rem 3.05rem;
  display: flex;
  flex-wrap: wrap;
  gap: .08rem .18rem;
  color: #8b8d9d;
  font-size: .24rem
}

.field-hint b {
  width: 100%;
  color: #686b7e;
  font-weight: 400
}

.writing-guide {
  width: 100%;
  margin-top: .2rem;
  padding: .25rem .28rem;
  color: #66697b;
  font-size: .26rem;
  line-height: 1.55;
  border-left: .07rem solid #B71C1C;
  background: #f5f6ff
}

.writing-guide b, .writing-guide span {
  display: block
}

.writing-guide b {
  margin-bottom: .08rem;
  color: #40435b;
  font-size: .28rem
}

.daily-logs-wrapper, .weekly-wrapper {
  width: 100%
}

.daily-logs-inputs, .weekly-inputs {
  width: 100%;
  margin-top: .25rem
}

.daily-log-entry, .weekly-log-entry {
  margin-bottom: .2rem
}

.daily-log-entry:last-child, .weekly-log-entry:last-child {
  margin-bottom: 0
}

.daily-log-entry label, .weekly-log-entry label {
  display: block;
  margin-bottom: .08rem;
  color: #55586c;
  font-size: .3rem
}

.daily-log-entry label em, .weekly-log-entry label em {
  margin-left: .04rem;
  color: #ee5c5c;
  font-style: normal
}

.daily-log-entry textarea, .weekly-log-entry textarea {
  width: 100%;
  min-height: 1.8rem;
  padding: .2rem;
  color: #44475b;
  font-size: .32rem;
  line-height: 1.55;
  border: 1PX solid #dddfea;
  border-radius: .14rem;
  background: #fafbfe
}

.daily-log-entry textarea:focus, .weekly-log-entry textarea:focus {
  border-color: @color
}

.tomorrow-plans, .next-week-plans {
  margin-top: .35rem
}

.tomorrow-plans h4, .next-week-plans h4 {
  margin-bottom: .15rem;
  padding-left: .18rem;
  color: #40435b;
  font-size: .32rem;
  border-left: .07rem solid #6369dd
}

.daily-tabs {
  display: flex;
  margin: .28rem 0 .12rem;
  padding: .08rem;
  background: #f5f6fb;
  border-radius: .16rem
}

.daily-tabs button {
  height: .72rem;
  flex: 1;
  color: #55586c;
  font-size: .31rem;
  border: 0;
  border-radius: .12rem;
  background: transparent
}

.daily-tabs button.active {
  color: #d32f2f;
  font-weight: 600;
  background: #fff;
  box-shadow: 0 .04rem .14rem rgba(211, 47, 47, .12)
}

.add-entry-btn {
  width: 100%;
  margin-top: .22rem;
  padding: .18rem 0;
  color: @color;
  font-size: .3rem;
  text-align: center;
  border: 1PX dashed #c5c7d8;
  border-radius: .14rem;
  background: #f8f9fc
}

.duty-float {
  position: fixed;
  z-index: 6;
  right: .16rem;
  top: 52%;
  width: .9rem;
  height: .9rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: .18rem;
  line-height: 1.15;
  border: 0;
  border-radius: 50%;
  background: @color;
  box-shadow: 0 .08rem .2rem rgba(211, 47, 47, .25)
}

.duty-float .van-icon {
  margin-bottom: .04rem;
  font-size: .3rem
}

.duty-popup {
  box-sizing: border-box;
  padding: .36rem .3rem;
  background: #fff
}

.duty-popup-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: .25rem;
  color: #34364a;
  font-size: .38rem;
  border-bottom: 1PX solid #eee
}

.duty-popup-title .van-icon {
  color: #999;
  font-size: .38rem
}

.duty-popup p {
  max-height: calc(70vh - 1.2rem);
  margin: 0;
  overflow-y: auto;
  padding-top: .3rem;
  color: #55586c;
  font-size: .31rem;
  line-height: 1.75;
  white-space: pre-wrap
}

.duty-float {
  box-sizing: border-box;
  width: 1rem;
  height: 1rem;
  padding: .1rem;
  overflow: hidden;
  font-size: .17rem;
  font-weight: 500;
  line-height: 1.12;
  text-align: center;
  white-space: normal;
  word-break: keep-all
}

.duty-float .van-icon {
  font-size: .27rem;
  line-height: 1
}

.binding-hint {
  margin: .08rem .28rem 0;
  color: #8b8d9d;
  font-size: .24rem;
  line-height: 1.5
}

.execution-binding .task-picker {
  padding-left: 0;
  padding-right: 0
}

.task-binding-panel {
  margin-top: .22rem;
  padding: .24rem;
  border: 1PX solid #f0d4d4;
  border-radius: .16rem;
  background: linear-gradient(180deg, #fffafa 0%, #fff 100%)
}

.task-binding-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .16rem;
  margin-bottom: .18rem
}

.task-type-tag {
  padding: .06rem .16rem;
  font-size: .22rem;
  border-radius: .2rem;
  background: #eef5ff;
  color: #1565c0
}

.task-type-tag.normal {
  background: #fff5f5;
  color: #c62828
}

.task-rate {
  font-size: .24rem;
  color: #667085
}

.task-binding-block label {
  display: block;
  margin-bottom: .08rem;
  color: #98a2b3;
  font-size: .23rem
}

.task-binding-block p {
  margin: 0;
  color: #44475b;
  font-size: .28rem;
  line-height: 1.6;
  white-space: pre-wrap
}

.task-binding-block.muted p {
  color: #667085;
  font-size: .26rem
}

.task-progress-hint {
  display: flex;
  gap: .12rem;
  align-items: flex-start;
  margin-top: .22rem;
  padding: .18rem .2rem;
  border-radius: .12rem;
  background: #f5f8ff;
  color: #475467;
  font-size: .24rem;
  line-height: 1.55
}

.task-progress-hint .van-icon {
  margin-top: .04rem;
  color: #2f6bd0;
  font-size: .28rem
}

.task-progress-hint p {
  margin: 0
}

.task-progress-slider {
  margin-top: .22rem;
  padding: .2rem .22rem .16rem;
  border-radius: .12rem;
  background: #fff;
  border: 1PX solid #f2e8e8
}

.slider-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: .14rem
}

.slider-head label {
  color: #55586c;
  font-size: .28rem
}

.slider-head strong {
  color: #c62828;
  font-size: .36rem;
  font-weight: 600
}

.slider-track {
  height: .12rem;
  margin-top: .12rem;
  border-radius: 999px;
  background: #f3e7e7;
  overflow: hidden
}

.slider-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #ef9a9a, #c62828);
  transition: width .15s ease
}

.slider-foot {
  margin: .14rem 0 0;
  color: #98a2b3;
  font-size: .22rem;
  line-height: 1.45
}

.collaborator-note {
  margin: .2rem 0;
  color: #77798a;
  font-size: .26rem;
  line-height: 1.55
}

.collaborator-required {
  margin: .16rem 0 0;
  color: #b71c1c;
  font-size: .25rem
}

.collaborator-selects {
  display: grid;
  gap: .14rem
}

.collaborator-selects label {
  display: grid;
  grid-template-columns:1.55rem 1fr;
  align-items: center;
  gap: .12rem;
  color: #555;
  font-size: .25rem
}

.collaborator-selects select {
  width: 100%;
  height: .72rem;
  padding: 0 .12rem;
  border: 1PX solid #ddd;
  border-radius: .08rem;
  background: #fff;
  color: #30313a;
  font-size: .25rem
}

.collaborator-order {
  display: flex;
  flex-wrap: wrap;
  gap: .12rem;
  margin-top: .22rem
}

.collaborator-order span {
  padding: .1rem .16rem;
  color: #b71c1c;
  font-size: .25rem;
  background: #fff2f2;
  border-radius: .1rem
}
</style>



// WEBPACK FOOTER //
// src/views/efficiency/WorklogForm.vue